<script setup lang="ts">
import type { SpacingProp } from '@/types/Layout';
import type Carousel from '@/components/widgets/Carousel.vue';
import type { AffiliateConfig } from '~/utils/perfilOficialConfigs';
import { useOfficialProfilesList } from '~/composables/apostatudo/use-official-profiles-list';

type DisplayMode = 'list' | 'slide';

type Props = {
  button?: { to: string; text: string };
  items?: { affiliateName: string; config: AffiliateConfig }[];
  title?: string;
  icon?: string;
  isLoading?: boolean;
  displayMode?: DisplayMode;
  /**
   * Items per row.
   * In order as number or number list
   *
   * itemsPerRow or [desktop, tablet, mobile]
   *
   * Ex: 8 or [8, 6, 4]
   */
  perRow?: SpacingProp;
  /**
   * Space between items.
   * In order as number or number list
   *
   * spaceBetweenItems or [desktop, tablet, mobile]
   *
   * Ex: 24 or [24, 16, 12]
   */
  spaceBetween?: SpacingProp;
  /**
   * Se true, carrega os perfis da API do core internamente
   * Se false ou undefined, usa a prop items
   */
  loadFromConfig?: boolean;
};

const {
  button,
  items: itemsProp,
  title,
  icon,
  displayMode = 'slide', // Default to slide mode for affiliates
  isLoading,
  perRow = [8, 6, 3], // Adjust itemsPerRow for desktop, tablet, mobile
  spaceBetween = [8, 14, 8],
  loadFromConfig = false,
} = defineProps<Props>();

// Perfis da API do core, com fallback pro config hardcoded (ver o composable)
const { items: officialProfiles, isLoading: isLoadingProfiles } =
  useOfficialProfilesList();

// Se loadFromConfig for true, carrega os dados internamente
const items = computed(() => {
  return loadFromConfig ? officialProfiles.value : itemsProp;
});

const isLoadingList = computed(
  () => isLoading || (loadFromConfig && isLoadingProfiles.value),
);

const { isMobile, getSpacingMeasurementFromPropList } = useLayout();
const { getLoadingStrategy } = useImageLoading();

const carouselRef = ref<InstanceType<typeof Carousel> | null>(null);

const isSlideMode = computed<boolean>(() => {
  return displayMode === 'slide';
});

const swiperRefForHeader = computed(() => {
  return isSlideMode.value ? carouselRef.value : null;
});

const spaceBetweenItems = computed<number>(() => {
  return getSpacingMeasurementFromPropList(spaceBetween, 16);
});

const spaceBetweenInPixels = computed<string>(() => {
  return spaceBetweenItems.value + 'px';
});

const itemsPerRow = computed<number>(() => {
  return getSpacingMeasurementFromPropList(perRow, 2);
});

const isSliderFadeVisible = computed<boolean>(() => {
  return isMobile.value && !!items.value && items.value.length > 3;
});

// Este componente NÃO está no sistema de tracking above fold
// Portanto, SEMPRE usa lazy loading (tudo 0)
const itemsAboveFold = computed<number>(() => {
  return 0; // TUDO LAZY
});
</script>

<template>
  <div :class="$style.listGames">
    <template v-if="isLoadingList">
      <div
        v-if="title"
        :class="$style.title"
      >
        <BaseCommonSkeleton :class="$style.skeletonTitle" />
      </div>

      <div :class="[$style.items]">
        <BaseCommonSkeleton
          v-for="i in isSlideMode ? itemsPerRow : 24"
          :key="i"
          :class="$style.skeletonItem"
        />
      </div>
    </template>

    <template v-else>
      <PerfilOficialSwiperHeader
        v-if="title || isSlideMode"
        :icon-path="icon"
        :see-more-text="button?.text"
        :see-more-url="button?.to"
        :swiper-ref="swiperRefForHeader"
        :title="title"
      />

      <div
        v-if="isSlideMode"
        class="w-full perfil-oficial-carousel-wrapper"
      >
        <LazyWidgetsCarousel
          ref="carouselRef"
          :display="{
            itemsPerRow,
            spaceBetweenItems,
          }"
          :item-class="$style.carouselItem"
          :items="items || []"
          :slider-fade="{ right: isSliderFadeVisible }"
        >
          <template #item="{ item, index }">
            <PerfilOficialCard
              :affiliate-name="item.affiliateName"
              :config="item.config"
              :loading="getLoadingStrategy(index, itemsAboveFold)"
            />
          </template>
        </LazyWidgetsCarousel>
      </div>

      <div
        v-else
        :class="[$style.items]"
      >
        <PerfilOficialCard
          v-for="(affiliate, index) in items"
          :key="affiliate.affiliateName"
          :affiliate-name="affiliate.affiliateName"
          :class="$style.mediaItem"
          :config="affiliate.config"
          :loading="getLoadingStrategy(index, itemsAboveFold)"
        />
      </div>
    </template>
  </div>
</template>

<style module lang="postcss">
.listGames {
  @apply flex flex-col select-none w-full;
}

.carouselItem {
  @media (min-width: 1024px) {
    width: auto;
    flex-basis: auto;
    flex-shrink: 0;
    flex-grow: 0;
  }
}

.items {
  @apply grid w-full;

  gap: v-bind(spaceBetweenInPixels);
  grid-template-columns: repeat(v-bind(itemsPerRow), minmax(0, 1fr));
}

.skeletonTitle {
  @apply h-[20px] max-w-[200px] flex-1;
}

.skeletonItem {
  @apply w-full h-auto aspect-[25/29];
}

/* Margin já aplicado no Carousel.vue */
</style>
